import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export const scrollToSection = (id) => {
  const target = document.getElementById(id);
  if (!target) {
    sessionStorage.setItem("jas_scroll_to", id);
    window.location.href = "/";
    return;
  }
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset: -72, duration: 1.4 });
  } else {
    target.scrollIntoView({ behavior: "smooth" });
  }
};

const Header = () => {
  const [open, setOpen] = useState(false);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToSection(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0B0F17]/80 backdrop-blur-xl"
      >
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
          <button
            data-testid="nav-logo"
            onClick={() => go("home")}
            className="group flex items-center text-left"
            aria-label="Jeet Advisory Services — home"
          >
            <img
              src="/logo-light.png"
              alt="Jeet Advisory Services"
              className="h-11 w-auto transition-opacity duration-300 group-hover:opacity-80"
            />
          </button>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
            {NAV_LINKS.filter((l) => l.id !== "home" && l.id !== "contact").map(
              (link) => (
                <button
                  key={link.id}
                  data-testid={`nav-link-${link.id}`}
                  onClick={() => go(link.id)}
                  className="text-[13px] font-light tracking-wide text-slate-400 transition-colors duration-300 hover:text-[#F26122]"
                >
                  {link.label}
                </button>
              )
            )}
            <button
              data-testid="nav-cta-discuss"
              onClick={() => go("contact")}
              className="group flex items-center gap-2 border border-[#F26122]/50 px-5 py-2.5 text-[13px] font-medium tracking-wide text-[#F26122] transition-all duration-300 hover:bg-[#F26122] hover:text-[#0B0F17]"
            >
              Discuss Your Requirement
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-slate-200 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-[#0B0F17]/98 px-8 backdrop-blur-2xl lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.id}
                  data-testid={`nav-mobile-link-${link.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  onClick={() => go(link.id)}
                  className="border-b border-white/5 py-4 text-left font-display text-3xl font-light text-slate-200 transition-colors hover:text-[#F26122]"
                >
                  {link.label}
                </motion.button>
              ))}
            </nav>
            <motion.button
              data-testid="nav-mobile-cta-discuss"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onClick={() => go("contact")}
              className="mt-10 border border-[#F26122]/60 px-6 py-4 text-sm tracking-[0.15em] text-[#F26122] uppercase"
            >
              Discuss Your Requirement
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
